import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import OrderServiceDocumentsField, { uploadOrderServiceDocuments } from '@/components/OrderServiceDocumentsField';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { buildContractPdfFilename } from '@/utils/contractDownload';
import { formatLooseDate, extractMarginDateChunk } from '@/utils/serviceDateDisplay';
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Clock,
  Phone,
  User,
  Truck,
  FileText,
  Edit,
  Check,
  X,
  RotateCcw,
  Trash2,
  Download,
  CalendarPlus,
  Image,
  Building,
  Route,
  Fuel,
  Printer,
  ExternalLink,
  Camera,
  Receipt,
  Upload,
  Loader2,
  Copy,
  CheckCircle,
  KeyRound
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Pickup / delivery WhatsApp OTP block for the admin order page.
 * Always rendered so the admin can immediately tell whether the code has been
 * generated (and read it back to a client/driver) or whether it's still pending.
 */
function OtpCodeBlock({ kind, code, verified, t }) {
  const isPickup = kind === 'pickup';
  const wrapperClass = isPickup
    ? 'border-green-300 bg-green-50'
    : 'border-red-300 bg-red-50';
  const codeColor = isPickup ? 'text-green-900' : 'text-red-900';
  const labelColor = isPickup ? 'text-green-800' : 'text-red-800';
  const labelKey = isPickup
    ? 'service_detail_pickup_otp_whatsapp'
    : 'service_detail_delivery_otp_whatsapp';

  const handleCopy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(String(code));
      toast.success(t('service_detail_otp_copied', { defaultValue: 'Código copiado' }));
    } catch {
      toast.error(t('service_detail_otp_copy_failed', { defaultValue: 'No se pudo copiar el código' }));
    }
  };

  return (
    <div className={`pt-2 border-t rounded-md border px-3 py-2 ${wrapperClass}`}>
      <div className="flex items-center justify-between gap-2">
        <p className={`text-xs font-semibold flex items-center gap-1.5 ${labelColor}`}>
          <KeyRound className="w-3.5 h-3.5" />
          {t(labelKey)}
        </p>
        {code && verified ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
            <CheckCircle className="w-3 h-3" />
            {t('service_detail_otp_verified', { defaultValue: 'Verificado' })}
          </span>
        ) : null}
      </div>
      {code ? (
        <div className="mt-1 flex items-center justify-between gap-2">
          <span className={`text-lg font-mono font-bold tracking-[0.35em] ${codeColor} select-all`}>
            {code}
          </span>
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
            aria-label={t('service_detail_otp_copy', { defaultValue: 'Copiar código' })}
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <p className="mt-1 text-sm text-gray-500 italic">
          {t('service_detail_otp_pending', {
            defaultValue: 'Pendiente — se generará al asignar conductor / iniciar peritaje.',
          })}
        </p>
      )}
    </div>
  );
}

/** Normalize API vehicle-photo entries (string URL, data URI, or { photo_data, photo_url }). */
function resolveVehiclePhotoSrc(photo, backendUrl) {
  if (photo == null) return '';
  if (typeof photo === 'string') {
    const s = photo.trim();
    if (!s) return '';
    if (s.startsWith('data:') || s.startsWith('http://') || s.startsWith('https://')) return s;
    return `${backendUrl}${s.startsWith('/') ? '' : '/'}${s.replace(/^\//, '')}`;
  }
  if (typeof photo === 'object') {
    const cand = photo.photo_data || photo.photo_url || photo.front_photo || photo.rear_photo;
    if (typeof cand === 'string' && cand.trim()) {
      const s = cand.trim();
      if (s.startsWith('data:') || s.startsWith('http://') || s.startsWith('https://')) return s;
      return `${backendUrl}${s.startsWith('/') ? '' : '/'}${s.replace(/^\//, '')}`;
    }
  }
  return '';
}

const ServiceDetailPage = () => {
  const params = useParams();
  // Route declares :serviceId; keep fallback for any legacy :id usage.
  const id = params.serviceId || params.id;
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [clients, setClients] = useState([]);
  const [additionalServices, setAdditionalServices] = useState([]);
  
  // Dialog states
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showPhotosDialog, setShowPhotosDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showDeleteServiceDialog, setShowDeleteServiceDialog] = useState(false);
  const [editOrderAttachmentFiles, setEditOrderAttachmentFiles] = useState([]);
  
  // Form states
  const [editForm, setEditForm] = useState({});
  const [completeForm, setCompleteForm] = useState({
    fuel: '',
    tolls: '',
    parking: '',
    taxi: '',
    other: '',
    other_description: '',
    completion_notes: ''
  });
  
  // Photos states
  const [pickupPhotos, setPickupPhotos] = useState([]);
  const [deliveryPhotos, setDeliveryPhotos] = useState([]);
  const [expensePhotos, setExpensePhotos] = useState([]);
  const [contractPhotos, setContractPhotos] = useState([]);
  const [contractPdfDownloading, setContractPdfDownloading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [vehiclePhotos, setVehiclePhotos] = useState(null);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  /** Prefetch for inline thumbnails on the order card (same API as «Ver Fotos»). */
  const [inlineVehiclePhotos, setInlineVehiclePhotos] = useState(null);
  const [loadingInlinePhotos, setLoadingInlinePhotos] = useState(false);
  const [clientPortalDocuments, setClientPortalDocuments] = useState([]);
  const [clientPortalDocUploading, setClientPortalDocUploading] = useState(false);
  const [approvingForBilling, setApprovingForBilling] = useState(false);

  // Driver search
  const [driverSearchTerm, setDriverSearchTerm] = useState('');
  const [showDriverOptions, setShowDriverOptions] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    if (!id) {
      toast.error(t('service_toast_load_failed'));
      navigate('/admin/dashboard');
      return;
    }
    loadServiceData();
  }, [id, navigate, t]);

  // Load driver peritación / completion photos so they appear on this page (not only behind «Ver Fotos»).
  // Always request: order.status varies (EN/ES, legacy values); vehicle_inspections may exist without flags.
  useEffect(() => {
    if (!id || !order) return;
    const token = localStorage.getItem('admin_token');
    if (!token) return;

    let cancelled = false;
    setLoadingInlinePhotos(true);
    (async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/admin/orders/${id}/vehicle-photos`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (response.ok) {
          const data = await response.json();
          setInlineVehiclePhotos(data?.has_photos ? data : null);
        } else {
          setInlineVehiclePhotos(null);
        }
      } catch {
        if (!cancelled) setInlineVehiclePhotos(null);
      } finally {
        if (!cancelled) setLoadingInlinePhotos(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, order?.id]);

  const loadServiceData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Load order, drivers, clients, additional services, and client-uploaded documents in parallel
      const [orderRes, driversRes, clientsRes, servicesRes, clientDocsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/orders/${id}`, { headers }),
        fetch(`${BACKEND_URL}/api/drivers`, { headers }),
        fetch(`${BACKEND_URL}/api/users`, { headers }),
        fetch(`${BACKEND_URL}/api/additional-services`, { headers }),
        fetch(`${BACKEND_URL}/api/orders/${id}/client-documents`, { headers })
      ]);

      if (orderRes.ok) {
        const orderData = await orderRes.json();
        setOrder(orderData);
        initializeEditForm(orderData);
      } else {
        toast.error(t('service_toast_load_failed'));
        navigate('/admin/dashboard');
      }

      if (driversRes.ok) {
        const driversData = await driversRes.json();
        setDrivers(driversData);
      }

      if (clientsRes.ok) {
        const clientsData = await clientsRes.json();
        setClients(clientsData.filter(u => u.role === 'client'));
      }

      if (servicesRes.ok) {
        const servicesData = await servicesRes.json();
        setAdditionalServices(servicesData);
      }

      if (clientDocsRes.ok) {
        const docsData = await clientDocsRes.json();
        setClientPortalDocuments(Array.isArray(docsData) ? docsData : []);
      } else {
        setClientPortalDocuments([]);
      }
    } catch (error) {
      console.error('Error loading service:', error);
      toast.error(t('service_toast_error_load'));
    } finally {
      setLoading(false);
    }
  };

  const initializeEditForm = (orderData) => {
    setEditForm({
      status: orderData.status || 'pending',
      driver_id: orderData.driver_id || '',
      car_plate: orderData.car_plate || '',
      car_brand: orderData.car_brand || '',
      car_model: orderData.car_model || '',
      pickup_address: orderData.pickup_address || '',
      delivery_address: orderData.delivery_address || '',
      pickup_contact_name: orderData.pickup_contact_name || '',
      pickup_contact_phone: orderData.pickup_contact_phone || '',
      delivery_contact_name: orderData.delivery_contact_name || '',
      delivery_contact_phone: orderData.delivery_contact_phone || '',
      pickup_date_from: orderData.pickup_date_from || '',
      pickup_time_start: orderData.pickup_time_start || '',
      pickup_time_end: orderData.pickup_time_end || '',
      delivery_date_from: orderData.delivery_date_from || '',
      delivery_time_start: orderData.delivery_time_start || '',
      delivery_time_end: orderData.delivery_time_end || '',
      distance_km: orderData.distance_km || 0,
      total_price: orderData.total_price || 0,
      driver_price: orderData.driver_price || 0,
      notes: orderData.notes || ''
    });
    
    const assignedDriver = drivers.find(d => d.id === orderData.driver_id);
    setDriverSearchTerm(assignedDriver ? assignedDriver.name : '');
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.company_name || client?.name || t('service_no_client');
  };

  const getDriverName = (driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    return driver?.name || t('service_no_driver');
  };

  const downloadAdminClientOrderDocument = async (orderId, docId, filename) => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(
        `${BACKEND_URL}/api/orders/${orderId}/client-documents/${docId}/file`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        toast.error(t('client_order_doc_download_fail'));
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      toast.error(t('client_order_doc_download_fail'));
    }
  };

  const uploadAdminClientOrderDocument = async (orderId, event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setClientPortalDocUploading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/orders/${orderId}/client-documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      let data = {};
      try {
        data = await res.json();
      } catch (_) {
        /* ignore */
      }
      const errMsg =
        typeof data.detail === 'string'
          ? data.detail
          : Array.isArray(data.detail) && data.detail[0]?.msg
            ? data.detail[0].msg
            : '';
      if (res.ok) {
        toast.success(t('admin_client_order_doc_upload_ok'));
        const listRes = await fetch(
          `${BACKEND_URL}/api/orders/${orderId}/client-documents`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (listRes.ok) {
          const list = await listRes.json();
          setClientPortalDocuments(Array.isArray(list) ? list : []);
        }
      } else {
        toast.error(errMsg || t('admin_client_order_doc_upload_fail'));
      }
    } catch (e) {
      console.error(e);
      toast.error(t('admin_client_order_doc_upload_fail'));
    } finally {
      setClientPortalDocUploading(false);
    }
  };

  const deleteAdminClientOrderDocument = async (orderId, docId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(
        `${BACKEND_URL}/api/orders/${orderId}/client-documents/${docId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      let data = {};
      try {
        data = await res.json();
      } catch (_) {
        /* ignore */
      }
      const errMsg = typeof data.detail === 'string' ? data.detail : t('admin_client_order_doc_delete_fail');
      if (res.ok) {
        toast.success(t('admin_client_order_doc_delete_ok'));
        setClientPortalDocuments((prev) => prev.filter((d) => d.id !== docId));
      } else {
        toast.error(errMsg);
      }
    } catch (e) {
      console.error(e);
      toast.error(t('admin_client_order_doc_delete_fail'));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return t('service_status_undefined');
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return t('service_status_undefined');
      return date.toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-GB', { 
        weekday: 'short', 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return t('service_status_undefined');
    }
  };

  const formatDateLoose = (raw) => {
    return formatLooseDate(raw, i18n.language === 'es' ? 'es-ES' : 'en-GB', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getDeliveryDateLabel = (ord) => {
    const structuredDate = formatDateLoose(ord.delivery_date_from);
    if (structuredDate) return structuredDate;

    const marginDateChunk = extractMarginDateChunk(ord.delivery_margin_time);
    if (marginDateChunk) return marginDateChunk;

    const pickupFallback = formatDateLoose(ord.pickup_date_to || ord.pickup_date_from);
    if (pickupFallback) return pickupFallback;

    const createdFallback = formatDateLoose(ord.created_at);
    if (createdFallback) return createdFallback;

    return t('no_delivery_date');
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5);
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { bg: 'bg-yellow-500', text: t('service_status_pending') },
      assigned: { bg: 'bg-blue-500', text: t('service_status_assigned') },
      in_progress: { bg: 'bg-purple-500', text: t('service_status_in_progress') },
      in_transit: { bg: 'bg-indigo-500', text: t('service_status_in_transit') },
      delivered: { bg: 'bg-green-500', text: t('service_status_delivered') },
      completed: { bg: 'bg-green-500', text: t('service_status_completed') },
      pending_approval: { bg: 'bg-amber-500', text: t('driver_pending_approval') },
      cancelled: { bg: 'bg-red-500', text: t('service_status_cancelled') }
    };
    return configs[status] || { bg: 'bg-gray-500', text: status };
  };

  // Action handlers
  const handleApproveForBilling = async () => {
    try {
      setApprovingForBilling(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${id}/approve`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ approval_status: 'approved' })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'approve failed');
      }
      toast.success(t('service_toast_updated'));
      await loadServiceData();
    } catch (e) {
      console.error(e);
      toast.error(e.message || t('service_toast_error_update'));
    } finally {
      setApprovingForBilling(false);
    }
  };

  const handleUpdateOrder = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        let docUploadErr = null;
        if (editOrderAttachmentFiles.length > 0) {
          try {
            await uploadOrderServiceDocuments(
              id,
              editOrderAttachmentFiles,
              BACKEND_URL,
              token,
              true
            );
          } catch (upErr) {
            console.error(upErr);
            docUploadErr = upErr.message || t('service_docs_upload_warn_generic');
          }
        }
        if (docUploadErr) {
          toast.warning(`${t('service_toast_updated')}: ${docUploadErr}`);
        } else {
          toast.success(t('service_toast_updated'));
        }
        setEditOrderAttachmentFiles([]);
        setShowEditDialog(false);
        loadServiceData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('service_toast_error_update'));
      }
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error(t('service_toast_error_update_service'));
    }
  };

  const handleUploadPhoto = async (event, category) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    setUploadingPhoto(true);
    try {
      const token = localStorage.getItem('admin_token');
      const uploadedUrls = [];

      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('photo_category', category);

        const response = await fetch(`${BACKEND_URL}/api/admin/orders/${id}/upload-completion-photo`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (response.ok) {
          const data = await response.json();
          uploadedUrls.push(`/api/photos/${data.photo_id}`);
        } else {
          toast.error(t('service_toast_error_upload_file', { fileName: file.name }));
        }
      }

      if (uploadedUrls.length > 0) {
        if (category === 'pickup') setPickupPhotos(prev => [...prev, ...uploadedUrls]);
        else if (category === 'delivery') setDeliveryPhotos(prev => [...prev, ...uploadedUrls]);
        else if (category === 'expense') setExpensePhotos(prev => [...prev, ...uploadedUrls]);
        else if (category === 'contract') setContractPhotos(prev => [...prev, ...uploadedUrls]);
        toast.success(t('service_toast_photos_uploaded', { count: uploadedUrls.length }));
      }
    } catch (error) {
      console.error('Error uploading photos:', error);
      toast.error(t('service_toast_error_upload_photos'));
    } finally {
      setUploadingPhoto(false);
      event.target.value = '';
    }
  };

  const handleCompleteService = async () => {
    const extractPhotoId = (url) => url.split('/').pop();

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${id}/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          expenses: {
            fuel: parseFloat(completeForm.fuel) || 0,
            tolls: parseFloat(completeForm.tolls) || 0,
            parking: parseFloat(completeForm.parking) || 0,
            taxi: parseFloat(completeForm.taxi) || 0,
            other: parseFloat(completeForm.other) || 0,
            other_description: completeForm.other_description
          },
          pickup_photos: pickupPhotos.map(extractPhotoId),
          delivery_photos: deliveryPhotos.map(extractPhotoId),
          expense_photos: expensePhotos.map(extractPhotoId),
          contract_photos: contractPhotos.map(extractPhotoId),
          completion_notes: completeForm.completion_notes
        })
      });

      if (response.ok) {
        toast.success(t('service_toast_completed'));
        setShowCompleteDialog(false);
        loadServiceData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('service_toast_error_complete'));
      }
    } catch (error) {
      console.error('Error completing service:', error);
      toast.error(t('service_toast_error_complete_service'));
    }
  };

  const handleRevertToPending = async () => {
    if (!window.confirm(t('service_confirm_revert'))) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${id}/revert-to-pending`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success(t('service_toast_reverted'));
        loadServiceData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('service_toast_error_revert'));
      }
    } catch (error) {
      console.error('Error reverting:', error);
      toast.error(t('service_toast_error_revert_service'));
    }
  };

  const executeDeleteOrder = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(t('service_toast_deleted'));
        navigate('/admin/dashboard');
      } else {
        const error = await response.json();
        toast.error(error.detail || t('service_toast_error_delete'));
      }
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(t('service_toast_error_delete_service'));
    }
  };

  const handleCancelOrder = async (cancelType = 'normal') => {
    try {
      const token = localStorage.getItem('admin_token');
      let endpoint = `${BACKEND_URL}/api/admin/orders/${id}/cancel`;
      let body = {};

      if (cancelType === 'pickup') {
        endpoint = `${BACKEND_URL}/api/admin/orders/${id}/cancel-at-pickup`;
      } else if (cancelType === 'transit') {
        endpoint = `${BACKEND_URL}/api/admin/orders/${id}/cancel-in-transit`;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        toast.success(t('service_toast_cancelled'));
        setShowCancelDialog(false);
        loadServiceData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('service_toast_error_cancel'));
      }
    } catch (error) {
      console.error('Error canceling:', error);
      toast.error(t('service_toast_error_cancel_service'));
    }
  };

  const handleDownloadContract = async () => {
    if (contractPdfDownloading) return;
    setContractPdfDownloading(true);
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        toast.error(t('service_toast_error_generate_contract'));
        return;
      }
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${id}/generate-contract?token=${encodeURIComponent(token)}&_=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      });

      if (response.ok) {
        const renderer = response.headers.get('X-AutoGo-Contract-Renderer');
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = buildContractPdfFilename(id);
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(t('service_toast_contract_downloaded'));
        if (renderer === 'reportlab') {
          toast.warning(t('service_toast_contract_fallback_simple_pdf'));
        }
      } else {
        let msg = t('service_toast_error_generate_contract');
        try {
          const err = await response.json();
          if (err.detail) msg = typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail);
        } catch { /* ignore */ }
        toast.error(msg);
      }
    } catch (error) {
      console.error('Error downloading contract:', error);
      // Fallback to direct browser download when fetch is blocked (e.g. proxy/CORS edge cases)
      const token = localStorage.getItem('admin_token');
      const contractUrl = `${BACKEND_URL}/api/admin/orders/${id}/generate-contract?token=${encodeURIComponent(token || '')}&_=${Date.now()}`;
      const link = document.createElement('a');
      link.href = contractUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.info(t('client_toast_preparing_contract'));
    } finally {
      setContractPdfDownloading(false);
    }
  };

  const handleAddToCalendar = () => {
    if (!order) return;
    
    const driver = drivers.find(d => d.id === order.driver_id);
    const driverName = driver ? driver.name : t('service_no_driver');
    
    const pickupCity = order.pickup_address ? order.pickup_address.split(',').pop().trim() : t('gps_live_na');
    const deliveryCity = order.delivery_address ? order.delivery_address.split(',').pop().trim() : t('gps_live_na');
    
    const title = `${order.car_plate || t('no_plate')} - ${driverName} - ${pickupCity} → ${deliveryCity}`;
    
    let pickupDate = order.pickup_date_from ? new Date(order.pickup_date_from) : new Date();
    if (order.pickup_time_start) {
      const [hours, minutes] = order.pickup_time_start.split(':');
      pickupDate.setHours(parseInt(hours) || 9, parseInt(minutes) || 0, 0, 0);
    }
    
    let deliveryDate = order.delivery_date_to ? new Date(order.delivery_date_to) : new Date(pickupDate.getTime() + 2 * 60 * 60 * 1000);
    if (order.delivery_time_end) {
      const [hours, minutes] = order.delivery_time_end.split(':');
      deliveryDate.setHours(parseInt(hours) || 18, parseInt(minutes) || 0, 0, 0);
    }
    
    const formatDateForGoogleCal = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}${month}${day}T${hours}${minutes}00`;
    };
    
    const details = t('service_calendar_event_body', {
      brand: order.car_brand || '',
      model: order.car_model || '',
      plate: order.car_plate || t('no_plate'),
      driver: driverName,
      pickup: order.pickup_address || '',
      delivery: order.delivery_address || '',
    });
    
    const baseUrl = 'https://calendar.google.com/calendar/render';
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: title,
      dates: `${formatDateForGoogleCal(pickupDate)}/${formatDateForGoogleCal(deliveryDate)}`,
      details: details,
      location: `${order.pickup_address} → ${order.delivery_address}`,
      trp: 'false'
    });
    
    window.open(`${baseUrl}?${params.toString()}`, '_blank');
    toast.success(t('service_toast_opening_calendar'));
  };

  const loadVehiclePhotos = async () => {
    setLoadingPhotos(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${id}/vehicle-photos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setVehiclePhotos(data);
      } else {
        setVehiclePhotos(null);
      }
    } catch (error) {
      console.error('Error loading photos:', error);
      setVehiclePhotos(null);
    } finally {
      setLoadingPhotos(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#36e961] mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('service_detail_loading')}</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">{t('service_not_found')}</p>
          <Button onClick={() => navigate('/admin/dashboard')} className="mt-4">
            {t('back_to_dashboard')}
          </Button>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(order.status);
  const isCancelled = order.status?.includes('Cancelado') || order.status === 'cancelled';
  const isDelivered = order.status === 'delivered' || order.status === 'completed';
  const approvalStatus = order.approval_status || 'pending';
  const approvalBadgeText =
    approvalStatus === 'approved'
      ? t('service_detail_approval_status_approved')
      : approvalStatus === 'rejected'
        ? t('service_detail_approval_status_rejected')
        : approvalStatus === 'pending_approval'
          ? t('service_detail_approval_status_pending_approval')
          : t('service_detail_approval_status_pending');
  const approvalBadgeClass =
    approvalStatus === 'approved'
      ? 'bg-emerald-500/90 text-white'
      : approvalStatus === 'rejected'
        ? 'bg-red-500/90 text-white'
        : approvalStatus === 'pending_approval'
          ? 'bg-amber-400 text-gray-900'
          : 'bg-yellow-400/90 text-gray-900';
  const showBillingGateNotice =
    !!order.driver_id &&
    approvalStatus !== 'approved' &&
    approvalStatus !== 'rejected' &&
    !order.include_in_driver_invoice;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-[#1a1f2e] text-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin/dashboard')}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('service_detail_back')}
              </Button>
              <div className="flex items-center gap-3">
                <div className="bg-[#36e961] p-2 rounded-lg">
                  <Truck className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold">
                    {t('service_detail_title', { id: id.substring(0, 8).toUpperCase() })}
                  </h1>
                  <p className="text-sm text-gray-300">{getClientName(order.client_id)}</p>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <span className={`${statusConfig.bg} px-4 py-1.5 rounded-full text-sm font-medium`}>
                {statusConfig.text}
              </span>
              <span
                className={`px-3 py-1.5 rounded-full text-xs font-semibold max-w-[min(100vw-8rem,22rem)] text-center leading-snug ${approvalBadgeClass}`}
                title={t('service_detail_approval_badge_label')}
              >
                {approvalBadgeText}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                className="border-white/30 text-white hover:bg-white/10"
              >
                <Printer className="w-4 h-4 mr-2" />
                {t('service_detail_print')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {showBillingGateNotice && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-950 shadow-sm">
            <p className="font-semibold text-sm">{t('service_detail_billing_requires_approval_title')}</p>
            <p className="text-sm mt-1 text-amber-900/90">{t('service_detail_billing_requires_approval_body')}</p>
            {approvalStatus === 'pending_approval' && (
              <Button
                type="button"
                className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                disabled={approvingForBilling}
                onClick={handleApproveForBilling}
              >
                {approvingForBilling ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('driver_saving')}
                  </>
                ) : (
                  t('service_detail_approve_for_billing')
                )}
              </Button>
            )}
          </div>
        )}
        {/* Summary Bar */}
        <Card>
          <CardContent className="py-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-sm text-gray-500">{t('service_detail_price_client_short')}</p>
                <p className="text-2xl font-bold text-green-600">€{order.total_price?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500">{t('service_detail_price_driver_short')}</p>
                <p className="text-2xl font-bold text-blue-600">€{order.driver_price?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500">{t('driver_billing_driver')}</p>
                <p className="text-lg font-semibold flex items-center justify-center gap-2">
                  <User className="w-4 h-4" />
                  {order.driver_id ? getDriverName(order.driver_id) : <span className="text-orange-500">{t('no_driver_assigned')}</span>}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500">{t('distance')}</p>
                <p className="text-lg font-semibold flex items-center justify-center gap-2">
                  <Route className="w-4 h-4" />
                  {order.distance_km ? `${order.distance_km} km` : t('service_status_not_calculated')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pickup and Delivery */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Pickup Card */}
          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-green-700">
                <div className="bg-green-100 p-2 rounded-full">
                  <MapPin className="w-5 h-5 text-green-600" />
                </div>
                {t('driver_pickup')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="font-medium text-gray-900">{order.pickup_address || t('address_not_specified')}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(order.pickup_date_from)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>
                    {formatTime(order.pickup_time_start) || '—'} 
                    {order.pickup_time_end && ` - ${formatTime(order.pickup_time_end)}`}
                  </span>
                </div>
              </div>
              {(order.pickup_contact_name || order.pickup_contact_phone) && (
                <div className="pt-2 border-t">
                  <p className="text-sm text-gray-500">{t('service_detail_contact')}</p>
                  <div className="flex items-center gap-4 mt-1">
                    {order.pickup_contact_name && (
                      <span className="flex items-center gap-1 text-sm">
                        <User className="w-3 h-3" />
                        {order.pickup_contact_name}
                      </span>
                    )}
                    {order.pickup_contact_phone && (
                      <a href={`tel:${order.pickup_contact_phone}`} className="flex items-center gap-1 text-sm text-blue-600 hover:underline">
                        <Phone className="w-3 h-3" />
                        {order.pickup_contact_phone}
                      </a>
                    )}
                  </div>
                </div>
              )}
              <OtpCodeBlock
                kind="pickup"
                code={order.pickup_confirmation_code}
                verified={order.pickup_code_verified}
                t={t}
              />
            </CardContent>
          </Card>

          {/* Delivery Card */}
          <Card className="border-l-4 border-l-red-500">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-red-700">
                <div className="bg-red-100 p-2 rounded-full">
                  <MapPin className="w-5 h-5 text-red-600" />
                </div>
                {t('driver_delivery')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="font-medium text-gray-900">{order.delivery_address || t('address_not_specified')}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>{getDeliveryDateLabel(order)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>
                    {order.delivery_time_start && order.delivery_time_end
                      ? `${formatTime(order.delivery_time_start)} - ${formatTime(order.delivery_time_end)}`
                      : (order.delivery_margin_time && order.delivery_margin_time.includes('|')
                          ? order.delivery_margin_time.split('|')[1]?.trim()
                          : (formatTime(order.delivery_time_start) || '—'))}
                  </span>
                </div>
              </div>
              <OtpCodeBlock
                kind="delivery"
                code={order.delivery_confirmation_code}
                verified={order.delivery_code_verified}
                t={t}
              />
              {(order.delivery_contact_name || order.delivery_contact_phone) && (
                <div className="pt-2 border-t">
                  <p className="text-sm text-gray-500">{t('service_detail_contact')}</p>
                  <div className="flex items-center gap-4 mt-1">
                    {order.delivery_contact_name && (
                      <span className="flex items-center gap-1 text-sm">
                        <User className="w-3 h-3" />
                        {order.delivery_contact_name}
                      </span>
                    )}
                    {order.delivery_contact_phone && (
                      <a href={`tel:${order.delivery_contact_phone}`} className="flex items-center gap-1 text-sm text-blue-600 hover:underline">
                        <Phone className="w-3 h-3" />
                        {order.delivery_contact_phone}
                      </a>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Vehicle Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              {t('service_detail_vehicle_heading')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <Truck className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p className="text-xs text-gray-500">{t('service_detail_type')}</p>
                <p className="font-medium">{order.car_type || t('car_type_turismo')}</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">{t('car_brand')}</p>
                <p className="font-medium text-lg">{order.car_brand || '—'}</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">{t('car_model')}</p>
                <p className="font-medium text-lg">{order.car_model || '—'}</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">{t('car_plate')}</p>
                <p className="font-bold text-lg font-mono">{order.car_plate || '—'}</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <Fuel className="w-5 h-5 mx-auto mb-1 text-gray-400" />
                <p className="text-xs text-gray-500">{t('fuel_type')}</p>
                <p className="font-medium">{order.fuel_type || t('service_detail_fuel_default')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Documents & Photos */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Documents */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {t('service_detail_documents_heading')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {order.driver_id ? (
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  disabled={contractPdfDownloading}
                  onClick={handleDownloadContract}
                >
                  {contractPdfDownloading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  {t('service_detail_download_transport_contract')}
                </Button>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  {t('service_detail_assign_driver_for_contract')}
                </p>
              )}

              <div className="pt-3 border-t border-gray-200">
                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                  {t('service_client_uploaded_files')}
                </p>
                <p className="text-xs text-gray-500 mb-2">{t('admin_client_order_doc_upload_hint')}</p>
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <input
                    type="file"
                    id="admin-client-order-doc-upload"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp,application/pdf"
                    onChange={(e) => uploadAdminClientOrderDocument(order.id, e)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                    disabled={clientPortalDocUploading}
                    onClick={() => document.getElementById('admin-client-order-doc-upload')?.click()}
                  >
                    {clientPortalDocUploading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    {t('admin_client_order_doc_upload')}
                  </Button>
                </div>
                {clientPortalDocuments.length === 0 ? (
                  <p className="text-sm text-gray-500">{t('service_client_uploaded_files_empty')}</p>
                ) : (
                  <ul className="space-y-2">
                    {clientPortalDocuments.map((doc) => (
                      <li
                        key={doc.id}
                        className="flex flex-wrap items-center justify-between gap-2 text-sm bg-gray-50 border border-gray-200 rounded-md px-3 py-2"
                      >
                        <div className="flex flex-col min-w-0 flex-1 gap-0.5">
                          <span className="text-gray-800 truncate" title={doc.original_filename}>
                            {doc.original_filename}
                          </span>
                          {doc.uploaded_by_role === 'admin' && (
                            <span className="text-[10px] uppercase tracking-wide text-amber-800 bg-amber-100 rounded px-1.5 py-0.5 w-fit">
                              {t('client_order_doc_badge_admin')}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              downloadAdminClientOrderDocument(order.id, doc.id, doc.original_filename)
                            }
                          >
                            <Download className="w-4 h-4 mr-1" />
                            {t('client_order_doc_download')}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 h-9 px-2"
                            onClick={() => deleteAdminClientOrderDocument(order.id, doc.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Photos */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5" />
                {t('service_detail_photos_heading')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {loadingInlinePhotos && (
                <p className="text-xs text-muted-foreground">{t('service_detail_loading_photos')}</p>
              )}
              {!loadingInlinePhotos && inlineVehiclePhotos?.has_photos && (
                <div className="space-y-2">
                  {inlineVehiclePhotos.pickup_photos?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-green-700 mb-1">
                        {t('service_detail_pickup_photos_heading')} ({inlineVehiclePhotos.pickup_photos.length})
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {inlineVehiclePhotos.pickup_photos.slice(0, 8).map((photo, idx) => {
                          const src = resolveVehiclePhotoSrc(photo, BACKEND_URL);
                          if (!src) return null;
                          return (
                            <button
                              type="button"
                              key={`in-p-${idx}`}
                              className="block rounded-md overflow-hidden border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#36e961]"
                              onClick={() => {
                                loadVehiclePhotos();
                                setShowPhotosDialog(true);
                              }}
                            >
                              <img
                                src={src}
                                alt=""
                                className="w-14 h-14 object-cover"
                              />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {inlineVehiclePhotos.delivery_photos?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-red-700 mb-1">
                        {t('service_detail_delivery_photos_heading')} ({inlineVehiclePhotos.delivery_photos.length})
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {inlineVehiclePhotos.delivery_photos.slice(0, 8).map((photo, idx) => {
                          const src = resolveVehiclePhotoSrc(photo, BACKEND_URL);
                          if (!src) return null;
                          return (
                            <button
                              type="button"
                              key={`in-d-${idx}`}
                              className="block rounded-md overflow-hidden border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#36e961]"
                              onClick={() => {
                                loadVehiclePhotos();
                                setShowPhotosDialog(true);
                              }}
                            >
                              <img
                                src={src}
                                alt=""
                                className="w-14 h-14 object-cover"
                              />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  loadVehiclePhotos();
                  setShowPhotosDialog(true);
                }}
              >
                <Image className="w-4 h-4 mr-2" />
                {t('service_detail_view_service_photos')}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Notes - aligned with admin/driver/client */}
        {(order.notes || order.first_vehicle_notes || order.second_vehicle_notes) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">📝 {t('admin_service_notes')}</CardTitle>
            </CardHeader>
            <CardContent className="bg-amber-50/50 border border-amber-200 rounded-lg space-y-3">
              {order.notes && (
                <div>
                  <p className="text-xs font-medium text-amber-700">{t('admin_notes_general')}</p>
                  <p className="text-gray-700 whitespace-pre-wrap mt-0.5">{order.notes}</p>
                </div>
              )}
              {order.first_vehicle_notes && (
                <div>
                  <p className="text-xs font-medium text-amber-700">{t('admin_notes_vehicle1')}</p>
                  <p className="text-gray-700 whitespace-pre-wrap mt-0.5">{order.first_vehicle_notes}</p>
                </div>
              )}
              {order.second_vehicle_notes && (
                <div>
                  <p className="text-xs font-medium text-amber-700">{t('admin_notes_vehicle2')}</p>
                  <p className="text-gray-700 whitespace-pre-wrap mt-0.5">{order.second_vehicle_notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <Card>
          <CardHeader>
            <CardTitle>{t('service_detail_actions_heading')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {/* Edit */}
              <Button
                variant="outline"
                onClick={() => {
                  setEditOrderAttachmentFiles([]);
                  initializeEditForm(order);
                  setShowEditDialog(true);
                }}
              >
                <Edit className="w-4 h-4 mr-2" />
                {t('service_detail_edit_service')}
              </Button>

              {/* Complete - only for non-delivered, non-cancelled */}
              {!isCancelled && !isDelivered && (
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => setShowCompleteDialog(true)}
                >
                  <Check className="w-4 h-4 mr-2" />
                  {t('service_detail_complete_service')}
                </Button>
              )}

              {/* Revert - only for delivered */}
              {isDelivered && (
                <Button
                  variant="outline"
                  className="border-orange-400 text-orange-700"
                  onClick={handleRevertToPending}
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  {t('service_detail_revert_to_in_progress')}
                </Button>
              )}

              {/* Calendar */}
              <Button
                variant="outline"
                onClick={handleAddToCalendar}
              >
                <CalendarPlus className="w-4 h-4 mr-2" />
                {t('service_detail_add_to_calendar')}
              </Button>

              {/* Cancel - only for non-cancelled, non-delivered */}
              {!isCancelled && !isDelivered && (
                <Button
                  variant="outline"
                  className="border-red-300 text-red-700"
                  onClick={() => setShowCancelDialog(true)}
                >
                  <X className="w-4 h-4 mr-2" />
                  {t('service_detail_cancel_service')}
                </Button>
              )}

              {/* Delete */}
              <Button
                variant="destructive"
                onClick={() => setShowDeleteServiceDialog(true)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t('delete')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={showDeleteServiceDialog} onOpenChange={setShowDeleteServiceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('admin_delete_dialog_title')}</DialogTitle>
            <DialogDescription>{t('admin_confirm_delete_service')}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setShowDeleteServiceDialog(false)}>
              {t('cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={async () => {
                setShowDeleteServiceDialog(false);
                await executeDeleteOrder();
              }}
            >
              {t('admin_delete_dialog_confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => {
        if (!open) setEditOrderAttachmentFiles([]);
        setShowEditDialog(open);
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('service_detail_edit_dialog_title')}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label>{t('service_detail_label_status')}</Label>
              <Select
                value={editForm.status}
                onValueChange={(value) => setEditForm({ ...editForm, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">{t('service_status_pending')}</SelectItem>
                  <SelectItem value="assigned">{t('service_status_assigned')}</SelectItem>
                  <SelectItem value="in_transit">{t('service_status_in_transit')}</SelectItem>
                  <SelectItem value="delivered">{t('service_status_delivered')}</SelectItem>
                  <SelectItem value="cancelled">{t('service_status_cancelled')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('service_detail_label_driver')}</Label>
              <Select
                value={editForm.driver_id || 'no-driver'}
                onValueChange={(value) => setEditForm({ ...editForm, driver_id: value === 'no-driver' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('placeholder_select_driver')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no-driver">{t('service_detail_no_driver_option')}</SelectItem>
                  {drivers.map((driver) => (
                    <SelectItem key={driver.id} value={driver.id}>
                      {driver.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('car_brand')}</Label>
              <Input
                value={editForm.car_brand}
                onChange={(e) => setEditForm({ ...editForm, car_brand: e.target.value })}
              />
            </div>
            <div>
              <Label>{t('car_model')}</Label>
              <Input
                value={editForm.car_model}
                onChange={(e) => setEditForm({ ...editForm, car_model: e.target.value })}
              />
            </div>
            <div>
              <Label>{t('car_plate')}</Label>
              <Input
                value={editForm.car_plate}
                onChange={(e) => setEditForm({ ...editForm, car_plate: e.target.value })}
              />
            </div>
            <div>
              <Label>{t('service_detail_label_distance_km')}</Label>
              <Input
                type="number"
                value={editForm.distance_km}
                onChange={(e) => setEditForm({ ...editForm, distance_km: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>{t('service_detail_label_client_price_eur')}</Label>
              <Input
                type="number"
                value={editForm.total_price}
                onChange={(e) => setEditForm({ ...editForm, total_price: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>{t('service_detail_label_driver_price_eur')}</Label>
              <Input
                type="number"
                value={editForm.driver_price}
                onChange={(e) => setEditForm({ ...editForm, driver_price: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="col-span-2">
              <Label>{t('service_detail_label_pickup_address')}</Label>
              <Input
                value={editForm.pickup_address}
                onChange={(e) => setEditForm({ ...editForm, pickup_address: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label>{t('service_detail_label_delivery_address')}</Label>
              <Input
                value={editForm.delivery_address}
                onChange={(e) => setEditForm({ ...editForm, delivery_address: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label>{t('service_detail_label_notes')}</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          <OrderServiceDocumentsField
            files={editOrderAttachmentFiles}
            onFilesChange={setEditOrderAttachmentFiles}
            disabled={false}
            onValidationError={(msg) => toast.error(msg)}
            labels={{
              fieldId: 'service-detail-edit-docs',
              title: t('service_docs_title'),
              hint: t('service_docs_hint_edit'),
              browse: t('service_docs_browse'),
              previewTitle: t('service_docs_preview'),
              remove: t('service_docs_remove'),
              maxFiles: t('service_docs_max_files'),
              fileTooLarge: t('service_docs_file_too_large'),
              invalidType: t('service_docs_invalid_type'),
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleUpdateOrder} className="bg-[#36e961] hover:bg-[#2dd351]">
              {t('update_order')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Service Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('service_detail_complete_dialog_title')}</DialogTitle>
            <DialogDescription>
              {t('service_detail_complete_dialog_desc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Expenses */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Receipt className="w-4 h-4" />
                {t('service_detail_expenses_heading')}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <Label>{t('driver_expense_fuel')} (€)</Label>
                  <Input
                    type="number"
                    value={completeForm.fuel}
                    onChange={(e) => setCompleteForm({ ...completeForm, fuel: e.target.value })}
                    placeholder={t('placeholder_amount')}
                  />
                </div>
                <div>
                  <Label>{t('driver_expense_tolls')} (€)</Label>
                  <Input
                    type="number"
                    value={completeForm.tolls}
                    onChange={(e) => setCompleteForm({ ...completeForm, tolls: e.target.value })}
                    placeholder={t('placeholder_amount')}
                  />
                </div>
                <div>
                  <Label>{t('service_detail_label_parking')}</Label>
                  <Input
                    type="number"
                    value={completeForm.parking}
                    onChange={(e) => setCompleteForm({ ...completeForm, parking: e.target.value })}
                    placeholder={t('placeholder_amount')}
                  />
                </div>
                <div>
                  <Label>{t('driver_expense_taxi')}</Label>
                  <Input
                    type="number"
                    value={completeForm.taxi}
                    onChange={(e) => setCompleteForm({ ...completeForm, taxi: e.target.value })}
                    placeholder={t('placeholder_amount')}
                  />
                </div>
                <div>
                  <Label>{t('driver_expense_other_label')}</Label>
                  <Input
                    type="number"
                    value={completeForm.other}
                    onChange={(e) => setCompleteForm({ ...completeForm, other: e.target.value })}
                    placeholder={t('placeholder_amount')}
                  />
                </div>
                <div>
                  <Label>{t('driver_expense_other_description')}</Label>
                  <Input
                    value={completeForm.other_description}
                    onChange={(e) => setCompleteForm({ ...completeForm, other_description: e.target.value })}
                    placeholder={t('ph_description')}
                  />
                </div>
              </div>
            </div>

            {/* Photos */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Camera className="w-4 h-4" />
                {t('service_detail_photos_section')}
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('service_detail_label_pickup_photos_count', { count: pickupPhotos.length })}</Label>
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleUploadPhoto(e, 'pickup')}
                    disabled={uploadingPhoto}
                  />
                </div>
                <div>
                  <Label>{t('service_detail_label_delivery_photos_count', { count: deliveryPhotos.length })}</Label>
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleUploadPhoto(e, 'delivery')}
                    disabled={uploadingPhoto}
                  />
                </div>
                <div>
                  <Label>{t('service_detail_label_expense_tickets_count', { count: expensePhotos.length })}</Label>
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleUploadPhoto(e, 'expense')}
                    disabled={uploadingPhoto}
                  />
                </div>
                <div>
                  <Label>{t('service_detail_label_signed_contract_count', { count: contractPhotos.length })}</Label>
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleUploadPhoto(e, 'contract')}
                    disabled={uploadingPhoto}
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label>{t('service_detail_label_completion_notes')}</Label>
              <Textarea
                value={completeForm.completion_notes}
                onChange={(e) => setCompleteForm({ ...completeForm, completion_notes: e.target.value })}
                placeholder={t('ph_observations_delivery')}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompleteDialog(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleCompleteService} 
              className="bg-green-600 hover:bg-green-700"
              disabled={uploadingPhoto}
            >
              <Check className="w-4 h-4 mr-2" />
              {t('service_detail_complete_service')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('service_detail_cancel_dialog_title')}</DialogTitle>
            <DialogDescription>
              {t('service_detail_cancel_dialog_desc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => handleCancelOrder('normal')}
            >
              <X className="w-4 h-4 mr-2" />
              {t('service_detail_cancel_normal')}
            </Button>
            {(order.status === 'assigned' || order.status === 'in_transit') && (
              <>
                <Button
                  variant="outline"
                  className="w-full justify-start border-orange-300"
                  onClick={() => handleCancelOrder('pickup')}
                >
                  🚫 {t('service_detail_cancel_at_pickup')}
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start border-red-300"
                  onClick={() => handleCancelOrder('transit')}
                >
                  🛑 {t('service_detail_cancel_in_transit')}
                </Button>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
              {t('service_detail_close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photos Dialog */}
      <Dialog open={showPhotosDialog} onOpenChange={setShowPhotosDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('service_detail_photos_dialog_title')}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {loadingPhotos ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#36e961] mx-auto"></div>
                <p className="mt-2 text-gray-500">{t('service_detail_loading_photos')}</p>
              </div>
            ) : vehiclePhotos &&
              (vehiclePhotos.pickup_photos?.length > 0 ||
                vehiclePhotos.delivery_photos?.length > 0 ||
                vehiclePhotos.contract_photos?.length > 0 ||
                vehiclePhotos.expense_photos?.length > 0) ? (
              <div className="space-y-6">
                {vehiclePhotos.pickup_photos?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2 text-green-700">{t('service_detail_pickup_photos_heading')}</h4>
                    <div className="grid grid-cols-3 gap-2">
                      {vehiclePhotos.pickup_photos.map((photo, idx) => {
                        const src = resolveVehiclePhotoSrc(photo, BACKEND_URL);
                        if (!src) return null;
                        return (
                          <img
                            key={typeof photo === 'object' && photo?.id ? photo.id : `p-${idx}`}
                            src={src}
                            alt={`${t('service_detail_pickup_photos_heading')} ${idx + 1}`}
                            className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-80"
                            onClick={() => window.open(src, '_blank')}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
                {vehiclePhotos.delivery_photos?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2 text-red-700">{t('service_detail_delivery_photos_heading')}</h4>
                    <div className="grid grid-cols-3 gap-2">
                      {vehiclePhotos.delivery_photos.map((photo, idx) => {
                        const src = resolveVehiclePhotoSrc(photo, BACKEND_URL);
                        if (!src) return null;
                        return (
                          <img
                            key={typeof photo === 'object' && photo?.id ? photo.id : `d-${idx}`}
                            src={src}
                            alt={`${t('service_detail_delivery_photos_heading')} ${idx + 1}`}
                            className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-80"
                            onClick={() => window.open(src, '_blank')}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
                {vehiclePhotos.contract_photos?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2 text-emerald-800">{t('admin_history_contract_photos')}</h4>
                    <div className="grid grid-cols-3 gap-2">
                      {vehiclePhotos.contract_photos.map((photo, idx) => {
                        const src = resolveVehiclePhotoSrc(photo, BACKEND_URL);
                        if (!src) return null;
                        return (
                          <img
                            key={typeof photo === 'object' && photo?.id ? photo.id : `c-${idx}`}
                            src={src}
                            alt={`${t('admin_history_contract_photos')} ${idx + 1}`}
                            className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-80"
                            onClick={() => window.open(src, '_blank')}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
                {vehiclePhotos.expense_photos?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2 text-orange-700">{t('admin_history_expense_photos')}</h4>
                    <div className="grid grid-cols-3 gap-2">
                      {vehiclePhotos.expense_photos.map((photo, idx) => {
                        const src = resolveVehiclePhotoSrc(photo, BACKEND_URL);
                        if (!src) return null;
                        return (
                          <img
                            key={typeof photo === 'object' && photo?.id ? photo.id : `e-${idx}`}
                            src={src}
                            alt={`${t('admin_history_expense_photos')} ${idx + 1}`}
                            className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-80"
                            onClick={() => window.open(src, '_blank')}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">{t('service_detail_no_photos')}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPhotosDialog(false)}>
              {t('service_detail_close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceDetailPage;
